import config from 'config/Config';
import {
  CloneRequestParams,
  CloneRequestResponse,
  ListAccountsResponse,
  OmniAuthTokenParams,
  SetPasswordParams,
  SignInParams,
  SignUpParams,
  SocialSignInParams,
  SocialSignUpParams,
  UserAttributes,
  VerifyEmailTokenParams,
} from 'types/user';
import { getHeadersFromLocalStorage, setHeadersToLocalStorage } from 'utils';
import { buildUrlWithQuery } from 'utils/helpers/urlHelpers';

import { api } from './setupApi';

export enum AuthModelFields {
  SPACE = 'clientName',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  PASSWORD = 'password',
  PASSWORD_CONFIRMATION = 'passwordConfirmation',
  TERMS = 'terms',
  HCAPTCHA = 'hCaptchaResponse',
  TOKEN = 'token',
}

interface SignUpResponse {
  message: string;
  resendEmailTimeout: null; // can be a date?
  resendEmailToken: string;
}

class AuthModel {
  private link = '/auth';

  private validation = `${this.link}/validate_token`;

  private signInLink = `${this.link}/sign_in`;

  private signUpLink = `${this.link}/sign_up`;

  private isSpaceNameUsedLilnk = `${this.link}/is_space_used`;

  private resendEmailLink = `${this.signUpLink}/resend_email`;

  private resendEmailTimoutLink = `${this.signUpLink}/resend_email_timeout`;

  private verifyEmailTokenLink = `${this.signUpLink}/verify_email_token`;

  private verifyBuilderTokenLink = `${this.signUpLink}/create_builder_user`;

  private setPasswordLink = `${this.signUpLink}/set_password`;

  private listAccountsLink = `${this.link}/omniauth/list_accounts`;

  private socialSignInLink = `${this.link}/omniauth/login_as_user`;

  private socialSignUpLink = `${this.link}/omniauth/sign_up`;

  private confirmCloningLink = `${this.link}/confirm_cloning`;

  private declineCloningLink = `${this.link}/decline_cloning`;

  private firstPromoterLink = '/first_promoters';

  readonly fields = {
    space: 'clientName',
    email: 'email',
    password: 'password',
    passwordConfirmation: 'passwordConfirmation',
    terms: 'terms',
    hCaptcha: 'hCaptchaResponse',
    token: 'token',
  };

  readonly googleFlow = {
    // TODO: use enum
    signIn: 'signin',
    signUp: 'signup',
    invite: 'invite',
  };

  signIn = (dataToSend: SignInParams): Promise<UserAttributes> =>
    api.post<Record<'data', UserAttributes>>(this.signInLink, dataToSend).then(({ data: { data }, headers }) => {
      setHeadersToLocalStorage(headers);
      return data;
    });

  signOut = () => api.delete(`${this.link}/sign_out`).then(({ data }) => data);

  verifyToken = (): Promise<UserAttributes> =>
    api
      .get<Record<'data', UserAttributes>>(buildUrlWithQuery(this.validation, getHeadersFromLocalStorage()))
      .then(({ data: { data } }) => data);

  signUp = (dataToSend: SignUpParams) => api.post<SignUpResponse>(this.signUpLink, dataToSend).then(({ data }) => data);

  resendEmail = (dataToSend: VerifyEmailTokenParams) =>
    api.post(`${this.resendEmailLink}`, dataToSend).then(({ data }) => data);

  getResendEmailTimeout = (dataToSend: VerifyEmailTokenParams) =>
    api.post(`${this.resendEmailTimoutLink}`, dataToSend).then(({ data }) => data);

  verifyEmailToken = (dataToSend: VerifyEmailTokenParams) =>
    api.post(`${this.verifyEmailTokenLink}`, dataToSend).then(({ data }) => data);

  verifyBuilderEmail = (dataToSend: { builder_token: string }) =>
    api.post<{ pubtoolToken: string }>(`${this.verifyBuilderTokenLink}`, dataToSend).then(({ data }) => data);

  setPassword = (dataToSend: SetPasswordParams) =>
    api.post(`${this.setPasswordLink}`, dataToSend).then(({ data }) => data);

  redirectToGoogleAuth = (flow: string = this.googleFlow.signIn, token?: string) => {
    window.location.href = `${config.api_host}/auth/google?flow=${flow}&token=${token}`;
  };

  socialSignUp = (params: SocialSignUpParams) => api.post(this.socialSignUpLink, params).then(({ data }) => data);

  socialSignIn = (params: SocialSignInParams) => api.post(this.socialSignInLink, params).then(({ data }) => data);

  listAccounts = (params: OmniAuthTokenParams) =>
    api.post<ListAccountsResponse>(this.listAccountsLink, params).then(({ data }) => data);

  confirmCloning = (params: CloneRequestParams): Promise<CloneRequestResponse> =>
    api.post<CloneRequestResponse>(this.confirmCloningLink, params).then(({ data }) => data);

  declineCloning = (params: CloneRequestParams): Promise<CloneRequestResponse> =>
    api.post<CloneRequestResponse>(this.declineCloningLink, params).then(({ data }) => data);

  trackFirstPromoterUser = (): Promise<void> => api.post(`${this.firstPromoterLink}`);

  isSpaceNameUsed = (name: string) => api.get(`${this.isSpaceNameUsedLilnk}/?client_name=${encodeURIComponent(name)}`);
}

export const authModel = new AuthModel();
